import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faNoteSticky } from "@fortawesome/free-solid-svg-icons";

import RouteHeader from "../common/RouteHeader";
import PageHeaderKebab from "../common/PageHeaderKebab";
/* ⚠️ A peça carrega o próprio CSS (2026-08-25). Ela desenha `.pnl-header` e
   `.pnl-header-titlewrap`, mas o `PanelHeader.css` só era importado por
   `PanelHeader.js` e `PanelPrivateHeader.js` — componentes DIFERENTES. Nas
   rotas `lazy` em que nenhum dos dois entra no chunk (o resultado de
   simulado é uma), o cabeçalho ficava sem `display:flex` e sem `gap`.
   Funcionava por herança de quem tivesse sido carregado antes. */
import "./PanelHeader.css";

/**
 * Fused header for the student drill-down panel. Renderiza no `.top` do
 * MainPage — via `headerComponent` do PrivateRoutesConfig (shim imediato
 * ao clicar na rota) OU via setPageHeader do próprio PanelStudent quando
 * o snapshot chega (versão com dados).
 *
 * <p>Full-path breadcrumb mirroring o mock (painel-aluno.html:83):
 * "Painel · Turmas · [class] · [student]". O aluno vira o crumb corrente
 * (bold); sem H1 separado — o hero card do body ancora a identidade.
 * Chevron à esquerda é o único back affordance.
 *
 * <p>Props opcionais (todas undefined = shim de loading):
 * @param {string}  [schoolName]   Nome da escola pro crumb multi-scope.
 * @param {boolean} [loading]      Explicit loading flag; undefined = true (shim).
 * @param {string}  [className]    Nome da turma (drill-down from classes).
 * @param {string}  [teacherName]  Nome do prof (drill-down from privateclasses).
 * @param {string}  [fullName]     Nome do aluno pro último crumb.
 * @param {string}  [backTo]       Rota de retorno; undefined = sem back-btn.
 * @param {node}    [title]        L2 abaixo dos crumbs. OPCIONAL e ausente por
 *   default: no drill-down do aluno o hero card do corpo já ancora a identidade,
 *   e um H1 ali duplicaria "quem sou eu". A tela de desempenho não tem hero —
 *   o corpo abre direto nas estatísticas —, então lá o L2 é quem diz O QUE está
 *   sendo visto. ⚠️ Quem passa `title` normalmente NÃO passa `fullName`: os dois
 *   juntos põem o mesmo nome no último crumb e no H1 logo abaixo.
 * @param {boolean} [fromPrivate]  Origin: true = privateclasses, false = classes.
 */
const PanelStudentHeader = ({
	schoolName,
	loading,
	className,
	teacherName,
	fullName,
	backTo,
	fromPrivate,
	title = null,
	noteCount,
	onOpenNotes,
}) => {
	const { t } = useTranslation();

	// Shim: sem props explícitos, loading = true por default.
	const isLoading = loading == null ? true : loading;

	// Crumb varies by origin panel — private drill-down reads "Aulas Particulares · Prof. X",
	// class drill-down reads "Turmas · {className}". Ambas as partes apontam pra `backTo`
	// (mesmo destino) — no separate "module home" between the drill-down and its list.
	const moduleLabel = fromPrivate
		? t('panel.private.crumb')
		: t('panel.student.crumb-classes');
	const contextLabel = fromPrivate
		? (teacherName ? t('panel.private.teacher-name', { name: teacherName }) : null)
		: className;

	// Loading state (Fatia C do loading system): partes ESTÁTICAS do crumb
	// ("Painel", "Turmas"/"Aulas Particulares", back button) permanecem
	// visíveis pra dar contexto imediato de navegação; partes que dependem
	// do fetch (nome da turma/prof, nome do aluno) viram shimmer inline.
	// Régua: mostrar apagado o que você sabe é pior que shimmerar o que
	// falta — o crumb estático confirma "cliquei certo".
	const shimmer = (w) => (
		<span
			className="sk"
			style={{ width: w, height: 12, borderRadius: 3, display: 'inline-block', verticalAlign: 'middle' }}
			aria-hidden="true"
		/>
	);

	// Nome do aluno vive nos crumbs (last item = bold parent). Sem H1 separado
	// porque o hero card do drill-down já ancora a identidade do aluno — evitar
	// duplicação de "quem sou eu" ali em cima E logo abaixo.
	// "Turmas · MENTORIA ENEM A+" (ou "Aulas Particulares · Prof. X") formam UM
	// crumb com separador interno tradicional (·) — hierarquia módulo→turma é fina
	// demais pra ocupar 2 slots do breadcrumb principal (que separa com /). Um único
	// link envolve o texto composto (mesmo destino pros dois lados).
	const contextLabelNode = isLoading ? shimmer(110) : contextLabel;
	/* 🔴 `pnl-crumb-hide-mobile` no módulo (2026-08-25). A regra que esconde
	   existe no `Panel.css` desde a fatia do header — mas NENHUM JS aplicava a
	   classe, então ela era CSS morto e o crumb completo continuava gastando
	   uma linha inteira do cabeçalho no aparelho.
	   ⚠️ O separador entra JUNTO no wrapper: escondendo só a palavra, a linha
	   começaria com " · ".
	   ⚠️ Só quando há `contextLabel`. Sem ele o módulo é o único crumb que
	   resta — escondê-lo deixaria o nível sem nome nenhum. */
	const moduleCrumbLabel = contextLabelNode
		? <><span className="pnl-crumb-hide-mobile">{moduleLabel}<span className="rh-crumbs-inner-sep"> · </span></span>{contextLabelNode}</>
		: moduleLabel;
	// School crumb first — só multi-scope (parent decide via prop).
	const crumbs = [
		/* 🔴 ESCOLA ou PAINEL, nunca os dois (PO, 2026-08-28) — mesma regra dos
		   outros dois cabeçalhos do painel. `schoolName` já é null quando há uma
		   escola só.
		   `Painel` some no mobile: é rótulo de MÓDULO, e a seta de voltar mais o
		   nome da turma já dizem onde se está. Some o crumb inteiro (com o
		   separador), não só o texto. */
		...(schoolName
			? [{ label: schoolName },
			   { label: t('header.navigation-titles.panel'), className: 'pnl-crumb-hide-mobile' }]
			: [{ label: t('header.navigation-titles.panel') }]),
		{ label: moduleCrumbLabel, to: backTo },
	];
	// ⚠️ Só shimmera o último crumb quem VAI ter um: com `title`, o nome vive no
	// H1, e um shimmer aqui prometeria um crumb que nunca chega.
	if (isLoading && !title) {
		crumbs.push({ label: shimmer(150) });
	} else if (fullName) {
		/* 📋 Ramo LEGADO: nenhum consumidor passa `fullName` desde 2026-08-28 — o
		   `PanelStudent` deixou de repetir o nome no cabeçalho (o card abaixo já
		   o mostra) e as outras duas telas sempre usaram `title`. Fica porque a
		   prop segue documentada; se ninguém a usar até o próximo pente, some
		   junto com ela. */
		crumbs.push({ label: fullName, className: 'pnl-crumb-hide-mobile' });
	}

	return (
		<div className="pnl-header pnls-header">
			{backTo && (
				<Link to={backTo} className="back-btn" aria-label={t('panel.student.back')}>
					<FontAwesomeIcon icon={faChevronLeft} />
				</Link>
			)}
			<div className="pnl-header-titlewrap">
				<RouteHeader crumbs={crumbs} title={title} />
			</div>

			{/* ⚠️ MESMO kebab do painel da turma, mesmo item — o que muda é o
			    SUJEITO: aqui a lista é sobre este aluno, e o campo `Aula` some do
			    compositor (§5.1: a anotação já tem sujeito, e a aula seria uma
			    terceira dimensão num modal de dois campos). */}
			{onOpenNotes && (
				<div className="pnl-header-kb">
					<PageHeaderKebab actions={[{
						handler: onOpenNotes,
						/* 🔴 O RÓTULO NOMEIA O ESCOPO (PO, 2026-09-10). Os três pontos de
						   entrada — turma, evento e aluno — abrem o MESMO modal com
						   conteúdos diferentes, e sem o escopo no rótulo os números se
						   contradizem: `· 2` aqui e `· 5` no header parecem defeito.
						   ✂️ Antes esta tela e o header da turma dividiam a chave
						   `kebab-see`: um nome para duas perguntas. */
						label: noteCount > 0
							? t('panel.notes.kebab-see-member', { count: noteCount })
							: t('panel.notes.kebab-write-member'),
						icon: faNoteSticky,
					}]} />
				</div>
			)}
		</div>
	);
};

export default PanelStudentHeader;
